// Keeps the local draw a live collaboration session writes into in step with
// the session itself. Fork-only.
//
// It is a hook rather than something inside `Collab.tsx` because the two
// things it joins up live on opposite sides: the room's terms come from the
// relay (`collabRoomAtom`), and the draw comes from the multi-file store,
// which is a React hook owned by App.tsx. Everything that actually touches
// IndexedDB is in `./collabSessionDoc.ts`; this file only decides when.

import { useCallback, useEffect, useRef } from "react";

import { appJotaiStore } from "../app-jotai";

import { collabSessionDocPendingAtom } from "./Collab";
import {
  clearCollabSessionDoc,
  discardCollabSessionDoc,
  openCollabSessionDoc,
  type CollabSessionDoc,
} from "./collabSessionDoc";
import { RETAIN_POLICIES, type RetainPolicy } from "./roomProtocol";

import type { CollabRoomState } from "./Collab";

/** The slice of `useMultiFileStore` a collaboration session needs. */
export interface CollabSessionDocStore {
  booted: boolean;
  activeDocId: string | null;
  adoptDoc: (docId: string) => Promise<void>;
  discardPendingSave: () => void;
  onActiveDeleted: () => Promise<void>;
}

export interface UseCollabSessionDocArgs {
  /** Off unless both the store and live collaboration are switched on. */
  enabled: boolean;
  /** The room this client is in, from `collabRoomAtom`. */
  room: CollabRoomState | null;
  store: CollabSessionDocStore;
  /** Localized name for a draw created to hold a joined room's scene. */
  docName: string;
}

/**
 * Wind one session's draw down.
 *
 * Under a discard policy the draw the session created is removed and the
 * editor is handed a different one; everything else (a draw the user shared,
 * a draw a retain policy lets them keep) only loses the room marker, so it
 * goes back to being an ordinary draw.
 */
const endCollabSession = async (
  session: CollabSessionDoc,
  retainPolicy: RetainPolicy,
  store: CollabSessionDocStore,
): Promise<void> => {
  if (retainPolicy === RETAIN_POLICIES.DISCARD) {
    // Whatever the debounce is still holding belongs to the draw that is about
    // to be gone, so it is dropped rather than written to whichever draw
    // takes its place.
    store.discardPendingSave();
    if (await discardCollabSessionDoc(session)) {
      if (store.activeDocId === session.docId) {
        // The draw the editor was showing no longer exists; the store opens a
        // stored fallback or a fresh untitled draw.
        await store.onActiveDeleted();
      }
      return;
    }
  }
  await clearCollabSessionDoc(session);
};

/**
 * Give a collaboration session a draw of its own, and clean it up afterwards.
 *
 * Runs entirely off the room state the relay reports, so it covers the ways a
 * session can end that no click produces: the room hitting its time limit, the
 * last peer leaving, a refused join.
 */
export const useCollabSessionDoc = ({
  enabled,
  room,
  store,
  docName,
}: UseCollabSessionDocArgs): void => {
  // Latest-value refs: both change identity on most renders, and neither is a
  // reason to re-run the lifecycle below.
  const storeRef = useRef(store);
  storeRef.current = store;
  const docNameRef = useRef(docName);
  docNameRef.current = docName;

  /** The draw this session writes into, once it has one. */
  const sessionRef = useRef<CollabSessionDoc | null>(null);
  /** The room whose draw we own, or are in the middle of opening. */
  const ownedRoomIdRef = useRef<string | null>(null);
  /** The server's latest word on the policy, read when the session ends. */
  const retainPolicyRef = useRef<RetainPolicy>(RETAIN_POLICIES.DISCARD);

  // One queue for the whole lifecycle, so an end can never overtake the open
  // it belongs to and leave a marked draw behind.
  const queueRef = useRef<Promise<void>>(Promise.resolve());
  const enqueue = useCallback((task: () => Promise<void>) => {
    queueRef.current = queueRef.current.then(task).catch((error) => {
      // eslint-disable-next-line no-console
      console.error("[drawOla] the collaboration draw lifecycle failed", error);
    });
  }, []);

  const booted = store.booted;

  useEffect(() => {
    if (!enabled) {
      return;
    }

    if (room === null) {
      const session = sessionRef.current;
      const retainPolicy = retainPolicyRef.current;
      ownedRoomIdRef.current = null;
      sessionRef.current = null;
      // Nothing is waiting for a draw any more, whether or not one was opened.
      appJotaiStore.set(collabSessionDocPendingAtom, false);
      if (session) {
        enqueue(() =>
          endCollabSession(session, retainPolicy, storeRef.current),
        );
      }
      return;
    }

    // The server's answer, refreshed by every `member_list`. Kept in a ref
    // rather than on the session, because the session object is created once
    // while this value can still change afterwards.
    retainPolicyRef.current = room.retainPolicy;

    if (ownedRoomIdRef.current === room.roomId) {
      return;
    }
    if (!booted) {
      // The store primes the canvas from the active draw at boot, and taking a
      // draw before that finishes would be undone by it. This effect re-runs
      // when `booted` flips.
      return;
    }

    const { roomId, entryMode } = room;
    ownedRoomIdRef.current = roomId;
    enqueue(async () => {
      const session = await openCollabSessionDoc({
        roomId,
        entryMode,
        activeDocId: storeRef.current.activeDocId,
        docName: docNameRef.current,
      });
      if (ownedRoomIdRef.current !== roomId) {
        // The session ended while its draw was still being resolved. Wind the
        // draw down here: the `room === null` branch had nothing to hand over.
        if (session) {
          await endCollabSession(
            session,
            retainPolicyRef.current,
            storeRef.current,
          );
        }
        return;
      }
      sessionRef.current = session;
      if (session && session.docId !== storeRef.current.activeDocId) {
        // `adoptDoc`, not `switchTo`: the room is the source of the scene and
        // hands it over itself, so replacing the canvas would throw it away.
        await storeRef.current.adoptDoc(session.docId);
      }
      appJotaiStore.set(collabSessionDocPendingAtom, false);
    });
  }, [booted, enabled, enqueue, room]);
};
